import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import '../../styles/pages.css';
import '../../styles/profile.css';

export default function Profile() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    email: '',
    entityName: '',
    entityPhone: '',
    entityEmail: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      // Use user.id if available (new logins), fallback to user.entity (existing sessions)
      const userId = user.id || user.entity;
      if (!userId) {
        setError('Unable to load profile. Please log in again.');
        return;
      }
      const { data } = await api.get(`/api/profile/${userId}`);
      setProfile(data);
      setFormData({
        firstName: data.FirstName,
        middleName: data.MiddleName || '',
        lastName: data.LastName,
        phone: data.Phone,
        email: data.Email,
        entityName: data.EntityName,
        entityPhone: data.EntityPhone,
        entityEmail: data.EntityEmail
      });
    } catch (err) {
      setError('Failed to load profile data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const userId = user.id || user.entity;
      await api.put('/api/profile/update', {
        userId,
        ...formData
      });
      setSuccess('Profile updated successfully!');
      setEditMode(false);
      setTimeout(() => setSuccess(''), 3000);
      fetchProfile();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      setSubmitting(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setSubmitting(false);
      return;
    }

    try {
      const userId = user.id || user.entity;
      await api.post('/api/profile/reset-password', {
        userId,
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      setSuccess('Password updated successfully!');
      setShowPasswordForm(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Profile</h1>
          <p>Manage your account profile</p>
        </div>
        <div className="page-section">
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Profile</h1>
        <p>Manage your account profile and organization details</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Organization Info */}
      <div className="page-section">
        <div className="section-header">
          <h3>Organization Information</h3>
          {!editMode && <button className="btn-secondary" onClick={() => setEditMode(true)}>Edit</button>}
        </div>

        {editMode ? (
          <form onSubmit={handleUpdateProfile} className="profile-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="entityName">Organization Name</label>
                <input
                  id="entityName"
                  type="text"
                  name="entityName"
                  value={formData.entityName}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="entityPhone">Organization Phone</label>
                <input
                  id="entityPhone"
                  type="tel"
                  name="entityPhone"
                  value={formData.entityPhone}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="entityEmail">Organization Email</label>
                <input
                  id="entityEmail"
                  type="email"
                  name="entityEmail"
                  value={formData.entityEmail}
                  onChange={handleFormChange}
                />
              </div>
            </div>
            <div className="profile-info-grid">
              <div className="info-item">
                <span className="label">Balance</span>
                <span className="value">${profile?.Balance || 0}</span>
              </div>
              <div className="info-item">
                <span className="label">Entity ID</span>
                <span className="value">{profile?.EntityId}</span>
              </div>
            </div>
          </form>
        ) : (
          <div className="profile-info-grid">
            <div className="info-item">
              <span className="label">Organization Name</span>
              <span className="value">{profile?.EntityName}</span>
            </div>
            <div className="info-item">
              <span className="label">Organization Phone</span>
              <span className="value">{profile?.EntityPhone}</span>
            </div>
            <div className="info-item">
              <span className="label">Organization Email</span>
              <span className="value">{profile?.EntityEmail}</span>
            </div>
            <div className="info-item">
              <span className="label">Balance</span>
              <span className="value">${profile?.Balance || 0}</span>
            </div>
            <div className="info-item">
              <span className="label">Entity ID</span>
              <span className="value">{profile?.EntityId}</span>
            </div>
          </div>
        )}
      </div>

      {/* User Profile */}
      <div className="page-section">
        <div className="section-header">
          <h3>User Account Information</h3>
        </div>

        {editMode ? (
          <form onSubmit={handleUpdateProfile} className="profile-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="middleName">Middle Name</label>
                <input
                  id="middleName"
                  type="text"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group form-group-full">
                <label htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setEditMode(false);
                  setFormData({
                    firstName: profile.FirstName,
                    middleName: profile.MiddleName || '',
                    lastName: profile.LastName,
                    phone: profile.Phone,
                    email: profile.Email,
                    entityName: profile.EntityName,
                    entityPhone: profile.EntityPhone,
                    entityEmail: profile.EntityEmail
                  });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="profile-info-grid">
            <div className="info-item">
              <span className="label">First Name</span>
              <span className="value">{profile?.FirstName}</span>
            </div>
            <div className="info-item">
              <span className="label">Middle Name</span>
              <span className="value">{profile?.MiddleName || '-'}</span>
            </div>
            <div className="info-item">
              <span className="label">Last Name</span>
              <span className="value">{profile?.LastName}</span>
            </div>
            <div className="info-item">
              <span className="label">Phone</span>
              <span className="value">{profile?.Phone}</span>
            </div>
            <div className="info-item">
              <span className="label">Email</span>
              <span className="value">{profile?.Email}</span>
            </div>
          </div>
        )}
      </div>

      {/* Password Reset */}
      <div className="page-section">
        <div className="section-header">
          <h3>Security</h3>
          {!showPasswordForm && (
            <button className="btn-secondary" onClick={() => setShowPasswordForm(true)}>
              Change Password
            </button>
          )}
        </div>

        {showPasswordForm && (
          <form onSubmit={handleResetPassword} className="profile-form">
            <div className="form-grid">
              <div className="form-group form-group-full">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  id="currentPassword"
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter your current password"
                  required
                />
              </div>
              <div className="form-group form-group-full">
                <label htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password (min. 6 characters)"
                  required
                />
              </div>
              <div className="form-group form-group-full">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  required
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Password'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {editMode && (
        <div className="form-actions" style={{ marginTop: '1rem' }}>
          <button type="submit" form="profile-form" className="btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save All Changes'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setEditMode(false)}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
